import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { useReprintApproval } from './ReprintApproval';
import IdentifyBox, { Identity } from './IdentifyBox';

// Settings → Printing (guided P&P phase 2, 2026-10-17; docs/GUIDED-PP.md). The station computers' print agents
// (print-agent/ in the repo), which Windows printer is the A4 (Brother) and which is each station's Zebra, test
// prints, and the recent jobs. Backend: backend/print_queue.py (/api/printing, /api/print-agent).

interface Queue { name: string; driver: string; status: string; port: string }
interface Agent {
  id: number; name: string; station_no: number | null; hostname: string | null; version: string | null;
  queues: Queue[] | null; last_seen_at: string | null; enrolled: boolean; online: boolean; code_pending: boolean;
}
interface Printer {
  id: number; agent_id: number; kind: 'a4' | 'zebra'; windows_queue: string; label: string | null;
  station_no: number | null; active: boolean; last_status: any; agent_name: string;
}
interface Job {
  id: number; kind: string; content_type: string; title: string | null; status: string; error: string | null;
  created_at: string; printed_at: string | null; created_by: string | null; printer_label?: string; reprint_of: number | null;
}

const card = 'bg-[#1a1d27] rounded-xl border border-gray-800 p-5';
const head = 'text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3';
const btn = 'px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-40';
const btnBlue = 'px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40';
const input = 'px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white text-sm';
const when = (s: string | null) => (s ? new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString() : '—');
const STATUS_CLS: Record<string, string> = {
  queued: 'text-gray-300', claimed: 'text-blue-300', printed: 'text-green-400', failed: 'text-red-400',
  unknown: 'text-amber-300', cancelled: 'text-gray-500',
};

async function errText(r: Response) {
  const d = await r.json().catch(() => ({} as any));
  return d.detail || `HTTP ${r.status}`;
}

type SetupFetch = (url: string, opts?: RequestInit) => Promise<Response>;
const UNLOCK_MIN = 10;          // a station's unlocked setup locks itself after this long without a change

export default function PrintingSettings() {
  const [data, setData] = useState<{ agents: Agent[]; printers: Printer[]; jobs: Job[]; can_edit: boolean; edit_with_pin?: boolean; agent_files: string[] } | null>(null);
  // At a P&P station (Fernando 21.10): the setup opens with an admin's PIN or badge. It is kept only in this page and
  // sent with every change (the server checks it each time, saves the change under the admin) — leaving the page or
  // UNLOCK_MIN minutes without a change locks it again.
  const [admin, setAdmin] = useState<{ hdr: Record<string, string>; name: string; at: number } | null>(null);
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!admin) return;
    const t = setTimeout(() => setAdmin(null), UNLOCK_MIN * 60000 - (Date.now() - admin.at));
    return () => clearTimeout(t);
  }, [admin]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [code, setCode] = useState<{ code: string; expires_at: string; agent: string } | null>(null);
  const [newName, setNewName] = useState('');
  const [newStation, setNewStation] = useState('');
  const reprintApproval = useReprintApproval();     // a reprint by Tier 1 needs a manager's PIN or badge (19.10)

  const load = () => authFetch(`${API}/printing`).then(async r => {
    if (!r.ok) { setErr(await errText(r)); return; }
    setData(await r.json());
  }).catch(() => setErr('Could not load'));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  if (!data) return <div className="text-gray-400 text-sm">{err || 'Loading…'}</div>;
  const canEdit = data.can_edit || !!admin;

  // every setup call: the admin's PIN / badge with it (a station); a refusal (wrong / locked PIN) locks the page again
  const setupFetch: SetupFetch = async (url, opts = {}) => {
    const r = await authFetch(url, { ...opts, headers: { ...((opts.headers as Record<string, string>) || {}), ...(admin?.hdr || {}) } });
    if (admin && (r.status === 403 || r.status === 428)) setAdmin(null);
    else if (admin && r.ok) setAdmin({ ...admin, at: Date.now() });
    return r;
  };
  const unlock = async (id: Identity) => {
    const hdr: Record<string, string> = id.badge ? { 'X-WMS-Badge': id.badge } : { 'X-WMS-Pin': id.pin || '' };
    const r = await authFetch(`${API}/printing/unlock`, { method: 'POST', headers: hdr });
    if (!r.ok) return errText(r);
    const d = await r.json().catch(() => ({} as any));
    setAdmin({ hdr, name: d.name || d.username || 'admin', at: Date.now() });
    setAsking(false); setErr('');
    return null;
  };
  const post = async (url: string, body?: any, method = 'POST') => {
    const r = await setupFetch(`${API}${url}`, { method, body: body ? JSON.stringify(body) : undefined });
    if (!r.ok) { setErr(await errText(r)); return null; }
    setErr('');
    return r.json().catch(() => ({}));
  };
  const addAgent = async () => {
    const d = await post('/printing/agents', { name: newName, station_no: newStation || null });
    if (d) { setCode({ code: d.code, expires_at: d.expires_at, agent: newName }); setNewName(''); setNewStation(''); load(); }
  };
  const newCode = async (a: Agent) => {
    const d = await post(`/printing/agents/${a.id}/enroll-code`);
    if (d) { setCode({ code: d.code, expires_at: d.expires_at, agent: a.name }); load(); }
  };
  const removeAgent = async (a: Agent) => {
    if (!window.confirm(`Remove "${a.name}"? Its printers stop receiving jobs; queued jobs fail.`)) return;
    if (await post(`/printing/agents/${a.id}`, undefined, 'DELETE')) { setMsg(`${a.name} removed.`); load(); }
  };
  const download = async (name: string) => {
    const r = await setupFetch(`${API}/printing/download/${encodeURIComponent(name)}`);
    if (!r.ok) { setErr(await errText(r)); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a'); a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const test = async (p: Printer) => {
    const d = await post(`/printing/printers/${p.id}/test`);
    if (d) { setMsg(`Test sent to ${p.label || p.windows_queue} (job ${d.id}).`); load(); }
  };
  const reprint = async (j: Job) => {
    const r = await reprintApproval.run(approver => authFetch(`${API}/printing/jobs/${j.id}/reprint`, {
      method: 'POST', body: JSON.stringify({ reason: 'reprint from Settings', ...approver }) }));
    if (!r) return;                                  // the approval was cancelled
    if (!r.ok) { setErr(await errText(r)); return; }
    const d = await r.json().catch(() => ({} as any));
    setErr(''); setMsg(`Reprint queued (job ${d.id}).`); load();
  };
  const resolve = async (j: Job, came_out: boolean) => {
    if (await post(`/printing/jobs/${j.id}/resolve`, { came_out })) load();
  };

  return (
    <div className="space-y-6">
      {reprintApproval.box}
      {asking && (
        <IdentifyBox title="Admin — identify yourself" cancelLabel="Cancel" onSubmit={unlock}
          onCancel={() => setAsking(false)}
          sub="This opens the print setup on this computer. Every change is saved under your name." />
      )}
      <div className={card}>
        <div className={head}>Printing</div>
        {data.edit_with_pin && (
          admin ? (
            <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-green-300">
              Unlocked for {admin.name} — every change is saved under {admin.name}. Locks itself after {UNLOCK_MIN} minutes without a change.
              <button className={btn} onClick={() => setAdmin(null)}>Lock now</button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-gray-300">
              Setting up this computer's printers needs an admin.
              <button className={btnBlue} onClick={() => setAsking(true)}>Unlock with admin PIN or badge</button>
            </div>
          )
        )}
        <p className="text-sm text-gray-400">
          Each P&P station computer runs a small print agent. The WMS sends it delivery notes for the <b className="text-gray-200">A4 printer</b>
          (a round's notes print at the station it was started at; a station without its own A4 prints on the other station's) and labels for the station's <b className="text-gray-200">Zebra</b>, and the agent prints
          them with no dialog. A job the agent took but never answered is <span className="text-amber-300">unknown</span> — it is never printed twice
          by itself; say whether it came out, or reprint.
        </p>
        {msg && <div className="text-sm text-green-400 mt-3">{msg}</div>}
        {err && <div className="text-sm text-red-400 mt-3">{err}</div>}
      </div>

      {code && (
        <div className={`${card} border-blue-700`}>
          <div className={head}>Enrolment code for “{code.agent}”</div>
          <div className="text-3xl font-mono font-bold text-white tracking-widest">{code.code}</div>
          <div className="text-xs text-gray-400 mt-1">Valid until {when(code.expires_at)} · shown once</div>
          <ol className="text-sm text-gray-300 mt-3 list-decimal list-inside space-y-1">
            <li>On the station computer, download the agent and the installer into one folder:
              {data.agent_files.map(f => <button key={f} className={`${btn} ml-2`} onClick={() => download(f)}>{f}</button>)}</li>
            <li>Open PowerShell <b>as administrator</b> in that folder and run: <code className="text-blue-300">powershell -ExecutionPolicy Bypass -File .\install.ps1</code></li>
            <li>Paste the code. The computer then shows as online below with its printers.</li>
          </ol>
          <button className={`${btn} mt-3`} onClick={() => setCode(null)}>Done</button>
        </div>
      )}

      <div className={card}>
        <div className={head}>Station computers</div>
        {data.agents.length === 0 && <div className="text-sm text-gray-500 mb-3">None yet.</div>}
        <div className="space-y-4">
          {data.agents.map(a => (
            <AgentBlock key={a.id} a={a} printers={data.printers.filter(p => p.agent_id === a.id)} canEdit={canEdit} setupFetch={setupFetch}
              onNewCode={() => newCode(a)} onRemove={() => removeAgent(a)} onTest={test}
              onSaved={m => { setMsg(m); setErr(''); load(); }} onError={setErr} />
          ))}
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2 items-center mt-4 pt-4 border-t border-gray-800">
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name, e.g. Station 1 laptop" className={`${input} w-56`} />
            <input value={newStation} onChange={e => setNewStation(e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="station no." className={`${input} keep-width w-24`} />
            <button className={btnBlue} disabled={!newName.trim()} onClick={addAgent}>Add station computer</button>
          </div>
        )}
        {canEdit && data.agents.length > 0 && (
          <div className="text-sm text-gray-400 mt-3">
            Update an enrolled computer (no code needed): download
            {data.agent_files.map(f => <button key={f} className={`${btn} ml-2`} onClick={() => download(f)}>{f}</button>)}
            <span className="ml-2">into one folder, then in PowerShell as administrator:</span>{' '}
            <code className="text-blue-300">powershell -ExecutionPolicy Bypass -File .\install.ps1 -Update</code>
          </div>
        )}
      </div>

      <AlertsCard />

      <div className={card}>
        <div className={head}>Recent print jobs</div>
        {data.jobs.length === 0 ? <div className="text-sm text-gray-500">None yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-1 pr-3">#</th><th className="py-1 pr-3">When</th><th className="py-1 pr-3">What</th>
                <th className="py-1 pr-3">Printer</th><th className="py-1 pr-3">Status</th><th className="py-1"></th></tr></thead>
              <tbody>
                {data.jobs.map(j => (
                  <tr key={j.id} className="border-b border-gray-800/60 align-top">
                    <td className="py-1 pr-3 text-gray-500">{j.id}{j.reprint_of ? <span className="text-xs"> (of {j.reprint_of})</span> : null}</td>
                    <td className="py-1 pr-3 text-gray-400 whitespace-nowrap">{when(j.created_at)}</td>
                    <td className="py-1 pr-3 text-gray-200">{j.title || j.kind}<div className="text-xs text-gray-500">{j.kind} · {j.content_type}{j.created_by ? ` · ${j.created_by}` : ''}</div></td>
                    <td className="py-1 pr-3 text-gray-300">{j.printer_label}</td>
                    <td className={`py-1 pr-3 ${STATUS_CLS[j.status] || 'text-gray-300'}`}>{j.status}
                      {j.error && <div className="text-xs text-red-300 max-w-xs">{j.error}</div>}</td>
                    <td className="py-1 whitespace-nowrap">
                      {j.status === 'unknown' && <>
                        <button className={btn} onClick={() => resolve(j, true)}>It came out</button>
                        <button className={`${btn} ml-1`} onClick={() => resolve(j, false)}>Did not</button></>}
                      {['failed', 'unknown', 'printed'].includes(j.status) && <button className={`${btn} ml-1`} onClick={() => reprint(j)}>Reprint</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Printer jam at a P&P station → an e-mail to these addresses (Fernando, 17.10: "printer jam needs a button Printer
// jam and the manager is going to be emailed"). Empty = no e-mail. Sent through Settings → Connectors → Mail.
function AlertsCard() {
  const [emails, setEmails] = useState('');
  const [approvalEmails, setApprovalEmails] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    authFetch(`${API}/pp2/settings/alerts`).then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setEmails((d.printer_problem_emails || []).join(', '));
                            setApprovalEmails((d.approval_emails || []).join(', ')); } }).catch(() => {});
  }, []);
  const list = (t: string) => t.split(/[,\s;]+/).map(x => x.trim()).filter(Boolean);
  const save = async () => {
    const r = await authFetch(`${API}/pp2/settings/alerts`, { method: 'PUT',
      body: JSON.stringify({ printer_problem_emails: list(emails), approval_emails: list(approvalEmails) }) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setMsg(d.detail || `HTTP ${r.status}`); return; }
    setEmails((d.printer_problem_emails || []).join(', '));
    setApprovalEmails((d.approval_emails || []).join(', ')); setMsg('✓ Saved');
  };
  return (
    <div className={card}>
      <div className={head}>Printer problems</div>
      <p className="text-sm text-gray-400 mb-2">When someone presses <b className="text-gray-200">Printer jam</b> at a P&P station, the round's delivery
        notes are printed again and an e-mail goes to these addresses (empty = no e-mail).</p>
      <div className="flex flex-wrap gap-2 items-center">
        <input value={emails} onChange={e => setEmails(e.target.value)} placeholder="manager@motleydenim.com, …" className={`${input} w-96`} />
      </div>
      <div className={`${head} mt-5`}>Approval waiting</div>
      <p className="text-sm text-gray-400 mb-2">When someone at a station needs a manager — <b className="text-gray-200">Pack without
        scanning</b> or a <b className="text-gray-200">reprint</b> — managers see an urgent notice in the WMS at once, and an e-mail goes
        to these addresses (empty = notice only; at most one mail per case every 15 minutes).</p>
      <div className="flex flex-wrap gap-2 items-center">
        <input value={approvalEmails} onChange={e => setApprovalEmails(e.target.value)} placeholder="manager@motleydenim.com, …" className={`${input} w-96`} />
        <button className={btnBlue} onClick={save}>Save</button>
        {msg && <span className="text-sm text-green-400">{msg}</span>}
      </div>
    </div>
  );
}

function AgentBlock({ a, printers, canEdit, setupFetch, onNewCode, onRemove, onTest, onSaved, onError }: {
  a: Agent; printers: Printer[]; canEdit: boolean; setupFetch: SetupFetch; onNewCode: () => void; onRemove: () => void;
  onTest: (p: Printer) => void; onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const queues = a.queues || [];
  const byQueue = new Map(printers.filter(p => p.active).map(p => [p.windows_queue, p]));
  const orphan = printers.filter(p => p.active && !queues.some(q => q.name === p.windows_queue));
  return (
    <div className="bg-[#0f1117] rounded-lg border border-gray-800 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`w-2.5 h-2.5 rounded-full ${a.online ? 'bg-green-400' : 'bg-red-500'}`} />
        <span className="text-white font-semibold">{a.name}</span>
        {a.station_no && <span className="text-xs text-gray-400">station {a.station_no}</span>}
        <span className="text-xs text-gray-500">
          {a.enrolled ? `${a.online ? 'online' : 'offline'} · last seen ${when(a.last_seen_at)}${a.hostname ? ` · ${a.hostname}` : ''}${a.version ? ` · v${a.version}` : ''}`
                      : (a.code_pending ? 'waiting for enrolment' : 'not enrolled — make a new code')}</span>
        {canEdit && <span className="ml-auto flex gap-1">
          <button className={btn} onClick={onNewCode}>New enrolment code</button>
          <button className={btn} onClick={onRemove}>Remove</button></span>}
      </div>
      {queues.length > 0 && (
        <div className="mt-3 space-y-1">
          {queues.map(q => (
            <QueueRow key={q.name} agent={a} q={q} printer={byQueue.get(q.name)} canEdit={canEdit} setupFetch={setupFetch} onTest={onTest} onSaved={onSaved} onError={onError} />
          ))}
        </div>
      )}
      {orphan.map(p => (
        <div key={p.id} className="text-xs text-red-300 mt-1">“{p.windows_queue}” ({p.kind}) is set up but the computer no longer reports it.</div>
      ))}
    </div>
  );
}

function QueueRow({ agent, q, printer, canEdit, setupFetch, onTest, onSaved, onError }: {
  agent: Agent; q: Queue; printer?: Printer; canEdit: boolean; setupFetch: SetupFetch; onTest: (p: Printer) => void;
  onSaved: (m: string) => void; onError: (m: string) => void;
}) {
  const [use, setUse] = useState(printer ? (printer.kind === 'a4' ? 'a4' : `zebra:${printer.station_no}`) : '');
  const [label, setLabel] = useState(printer?.label || '');
  const save = async () => {
    if (printer) {
      const r = await setupFetch(`${API}/printing/printers/${printer.id}`, { method: 'PUT', body: JSON.stringify({ active: false }) });
      if (!r.ok) { onError(await errText(r)); return; }
    }
    if (use) {
      const [kind, st] = use.split(':');
      const r = await setupFetch(`${API}/printing/printers`, { method: 'POST', body: JSON.stringify({
        agent_id: agent.id, kind, windows_queue: q.name, label: label || null, station_no: kind === 'zebra' ? Number(st) : agent.station_no }) });
      if (!r.ok) { onError(await errText(r)); return; }
    }
    onSaved(use ? `“${q.name}” is now ${use === 'a4' ? 'the A4 printer' : `station ${use.split(':')[1]}'s label printer`}.` : `“${q.name}” is not used any more.`);
  };
  const stations = [1, 2, 3];
  const current = printer ? (printer.kind === 'a4' ? 'a4' : `zebra:${printer.station_no}`) : '';
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-gray-200 w-72 truncate" title={`${q.name} · ${q.driver} · ${q.port}`}>{q.name}</span>
      <span className="text-xs text-gray-500 w-24">{q.status}</span>
      {canEdit ? (
        <>
          <select value={use} onChange={e => setUse(e.target.value)} className={input}>
            <option value="">— not used —</option>
            <option value="a4">A4 printer (delivery notes)</option>
            {stations.map(s => <option key={s} value={`zebra:${s}`}>Label printer — station {s}</option>)}
          </select>
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="label (optional)" className={`${input} w-36`} />
          {use !== current && <button className={btnBlue} onClick={save}>Save</button>}
        </>
      ) : (
        <span className="text-xs text-gray-400">{printer ? (printer.kind === 'a4' ? 'A4 printer' : `label printer — station ${printer.station_no}`) : 'not used'}</span>
      )}
      {printer && <button className={btn} onClick={() => onTest(printer)}>Test</button>}
    </div>
  );
}
